import {
  Badge,
  Card,
  Flex,
  Grid,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip as MantineTooltip,
} from '@mantine/core';
import { memo, useMemo } from 'react';
import { PipelineVerificationCard } from '../../ai-verification/PipelineVerificationCard';
import type { EffectiveSessionDataSource } from '../../services/resolveEffectiveSessionValidations';
import type {
  ExcludedSessionReason,
  FeatureKey,
  SpectralAnalysisSnapshot,
  ValidatedSessionAnalysis,
} from '../../types';
import { computeOverviewEventMetrics } from '../../utils/computeOverviewEventMetrics';
import {
  getSessionEventBadge,
  getSessionEventTooltip,
  type SessionAnalysisDisplayState,
} from '../../utils/sessionEventDisplay';

const featureKeys: FeatureKey[] = ['HR', 'HRV', 'LF', 'HF'];

const featureCoverageColor = (score: number) => {
  if (score >= 90) return 'green';
  if (score >= 70) return 'yellow';
  return 'red';
};

const usesHrvPreprocessing = (
  summary: SpectralAnalysisSnapshot['includedSessions'][number]['spectral']['preprocessingSummary']
) => summary.preprocessingVersion === 2;

type RecalculatedSessionState =
  | {
      source: 'repaired' | 'effective';
      validation: ValidatedSessionAnalysis;
      excluded?: undefined;
    }
  | {
      source: 'repaired' | 'effective';
      validation?: undefined;
      excluded: ExcludedSessionReason;
    };

type SpectralOverviewTabProps = {
  snapshot: SpectralAnalysisSnapshot;
  sessionAnalysisById: Record<number, SessionAnalysisDisplayState>;
  recalculatedSessions: Record<number, RecalculatedSessionState>;
  sessionDataSourceById: Record<number, EffectiveSessionDataSource>;
  effectiveValidationsLoading: boolean;
  formatDate: (value?: string | null) => string;
};

export const SpectralOverviewTab = memo(function SpectralOverviewTab({
  snapshot,
  sessionAnalysisById,
  recalculatedSessions,
  sessionDataSourceById,
  effectiveValidationsLoading,
  formatDate,
}: SpectralOverviewTabProps) {
  const { perfectSessions, sessionsWithEventIssues } = useMemo(
    () =>
      computeOverviewEventMetrics(
        snapshot,
        sessionAnalysisById,
        recalculatedSessions,
        sessionDataSourceById
      ),
    [snapshot, sessionAnalysisById, recalculatedSessions, sessionDataSourceById]
  );

  const outlierSessionIds = useMemo(
    () =>
      snapshot.includedSessions
        .filter(item => item.spectral.outlier)
        .map(item => item.validation.sessionId),
    [snapshot.includedSessions]
  );

  const getEffectiveValidation = (
    item: SpectralAnalysisSnapshot['includedSessions'][number]
  ) =>
    recalculatedSessions[item.validation.sessionId]?.validation ??
    item.validation;

  const getEffectiveExcluded = (
    item: SpectralAnalysisSnapshot['includedSessions'][number]
  ) => recalculatedSessions[item.validation.sessionId]?.excluded;

  const isSessionRepairStateLoading = (sessionId: number) =>
    effectiveValidationsLoading && !(sessionId in sessionDataSourceById);

  return (
    <Stack gap='md'>
      <Card withBorder>
        <Flex
          justify='space-between'
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap='sm'
        >
          <div>
            <Title order={4}>Normalizzazione spettrale</Title>
            <Text size='sm' c='dimmed'>
              Autore: {snapshot.author} | Data: {formatDate(snapshot.createdAt)}{' '}
              | Sessioni: {snapshot.groupSummary.includedSessions}/
              {snapshot.selectedSessions.length} incluse
            </Text>
          </div>
        </Flex>
      </Card>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder h='100%'>
            <Title order={5}>Sessioni perfette</Title>
            <Text size='xl' fw={700}>
              {perfectSessions}/{snapshot.groupSummary.includedSessions}
            </Text>
            <Text c='dimmed' size='sm'>
              {sessionsWithEventIssues} con gap o duplicati (post-analisi)
            </Text>
            <Text c='dimmed' size='sm' mt='xs'>
              Sessioni senza problemi rilevati nei record eventi, utili per una
              lettura più lineare del campione.
            </Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder h='100%'>
            <Title order={5}>HR delta medio</Title>
            <Text size='xl' fw={700}>
              {snapshot.groupSummary.averageHrDelta} ±{' '}
              {snapshot.groupSummary.hrDeltaStd} bpm
            </Text>
            <Text c='dimmed' size='sm' mt='xs'>
              Differenza media della frequenza cardiaca tra fase iniziale e fase
              finale nelle sessioni incluse. Il simbolo ± indica quanto i valori
              variano tra i partecipanti.
            </Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder h='100%'>
            <Title order={5}>LF/HF delta</Title>
            <Text size='xl' fw={700}>
              {snapshot.groupSummary.averageLfHfDelta} ±{' '}
              {snapshot.groupSummary.lfHfDeltaStd}
            </Text>
            <Text c='dimmed' size='sm' mt='xs'>
              Differenza media del rapporto LF/HF tra inizio e fine sessione.
              Aiuta a leggere come cambia l’equilibrio spettrale nel gruppo.
            </Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder h='100%'>
            <Title order={5}>Sessioni anomale nel gruppo</Title>
            <Text size='xl' fw={700}>
              {snapshot.groupSummary.outlierCount} sessioni
            </Text>
            <Text c='dimmed' size='sm' mt='xs'>
              Sessioni che si discostano in modo marcato dall’andamento medio
              del gruppo e che possono influenzare la lettura dei risultati.
            </Text>
            <Text c='dimmed' size='sm' mt='xs'>
              {outlierSessionIds.length > 0
                ? `Sessioni coinvolte: ${outlierSessionIds.join(', ')}`
                : 'Nessuna sessione anomala rilevata in questa analisi.'}
            </Text>
          </Card>
        </Grid.Col>
      </Grid>

      <Group gap='xs' mb='xs'>
        <Badge color='indigo' variant='light'>
          Pipeline HRV preprocessing (NN da HR, 4 Hz)
        </Badge>
        <Text size='sm' c='dimmed'>
          Tachogramma proxy Polar Loop 360; RMSSD da ΔRR; soglia artefatti 5%
          (Kubios-style).
        </Text>
      </Group>

      <PipelineVerificationCard key={snapshot.analysisId} snapshot={snapshot} />

      <Card withBorder>
        <Title order={4} mb='sm'>
          Heatmap eventi
        </Title>
        <Text size='sm' c='dimmed' mb='md'>
          La colonna Eventi mostra gap e duplicati solo dopo l&apos;analisi sui
          dati originali.
        </Text>
        <ScrollArea>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sessione</Table.Th>
                <Table.Th>Utente</Table.Th>
                {featureKeys.map(feature => (
                  <Table.Th key={feature}>{feature}</Table.Th>
                ))}
                <Table.Th>Eventi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {snapshot.includedSessions.map(item => {
                const sessionId = item.validation.sessionId;
                const effectiveValidation = getEffectiveValidation(item);
                const effectiveExcluded = getEffectiveExcluded(item);
                const usingRepaired =
                  recalculatedSessions[sessionId]?.source === 'repaired';
                const analysis = sessionAnalysisById[sessionId];
                const isRepaired =
                  sessionDataSourceById[sessionId] === 'hrData_repaired' ||
                  usingRepaired;
                const isLoadingState = isSessionRepairStateLoading(sessionId);
                const eventBadge = getSessionEventBadge({
                  analysis,
                  isRepaired,
                  isExcluded: Boolean(effectiveExcluded),
                  isLoading: isLoadingState,
                });

                return (
                  <Table.Tr key={sessionId}>
                    <Table.Td>{item.validation.sessionId}</Table.Td>
                    <Table.Td>
                      <Group gap='xs'>
                        <Text>{item.validation.patientId}</Text>
                        {usingRepaired && (
                          <Badge size='xs' variant='light' color='teal'>
                            riparata
                          </Badge>
                        )}
                        {usesHrvPreprocessing(
                          item.spectral.preprocessingSummary
                        ) && (
                          <Badge size='xs' variant='outline' color='indigo'>
                            HRV preprocessing
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    {featureKeys.map(feature => {
                      const score = effectiveExcluded
                        ? 0
                        : effectiveValidation.quality.validDataPercentByFeature[
                            feature
                          ];
                      return (
                        <Table.Td key={feature}>
                          <Badge
                            color={featureCoverageColor(score)}
                            variant='light'
                          >
                            {effectiveExcluded ? 'esclusa' : `${score}%`}
                          </Badge>
                        </Table.Td>
                      );
                    })}
                    <Table.Td>
                      {isLoadingState ? (
                        <Skeleton height={26} radius='xl' width={128} />
                      ) : (
                        <MantineTooltip
                          multiline
                          w={360}
                          label={getSessionEventTooltip(
                            analysis,
                            Boolean(effectiveExcluded),
                            isLoadingState
                          )}
                        >
                          <Badge
                            color={eventBadge.color}
                            variant={eventBadge.variant}
                            style={{ cursor: 'help' }}
                          >
                            {eventBadge.label}
                          </Badge>
                        </MantineTooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
});
